import React, { useState, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8000";

const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const renderEventCard = (event) => (
  <div 
    key={event.id} 
    className="event-card-large p-4 sm:p-5 md:p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300" 
    onClick={() => window.location.href = `/event-details/${event.id}`} 
    style={{cursor:'pointer'}}
  >
    <div className="event-content flex flex-col h-full">
      <div className="event-image-container mb-4">
        <img 
          src={event.thumbnail ? `${API_URL}${event.thumbnail}` : 'https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg'} 
          className="event-image w-full h-48 sm:h-56 md:h-64 lg:h-72 object-cover rounded-lg" 
          alt={event.name} 
        />
      </div>

      <div className="event-category text-xs sm:text-sm md:text-base font-medium text-blue-600 mb-2">
        {event.category}
      </div>
      
      <h3 className="event-title text-lg sm:text-xl md:text-2xl font-bold mb-3 text-gray-800 line-clamp-2">
        {event.name}
      </h3>
      
      <div className="event-details mt-auto">
        <div className="flex items-center text-sm sm:text-base md:text-lg text-gray-600 mb-3">
          <i className="bi-stopwatch mr-2"></i> 
          {formatDateTime(event.start_date)}
          {event.end_date && (
            <>
              {" "}- {formatDateTime(event.end_date)}
            </>
          )}
        </div>
        
        {event.status && (
          <span 
            className={`event-status-badge-pending event-status-${event.status.toLowerCase()} inline-block text-xs sm:text-sm md:text-base px-3 py-1 rounded-full font-medium`}
          >
            {event.status}
          </span>
        )}
      </div>
    </div>
  </div>
);

const LatestEventsSection = ({ count = 3 }) => {
  const [latestEvents, setLatestEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  useEffect(() => {
    fetchLatestEvents();
    // eslint-disable-next-line
  }, [count]);

  const fetchLatestEvents = async () => {
    try {
      const response = await fetch(`${API_URL}/api/events/latest/?count=${count}`);
      if (response.ok) {
        const data = await response.json();
        setLatestEvents(data);
      } else {
        setError('Failed to fetch events');
      }
    } catch (err) {
      setError('Network error while fetching events');
    } finally {
      setLoading(false);
    }
  };

  // Swipe handlers for mobile
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentMobileIndex < latestEvents.length - 1) {
      setCurrentMobileIndex(currentMobileIndex + 1);
    }
    if (isRightSwipe && currentMobileIndex > 0) {
      setCurrentMobileIndex(currentMobileIndex - 1);
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  return (
    <div className="events-container px-4 sm:px-6 md:px-8 lg:px-12">
      <div className="max-width full-width px-4 sm:px-6 md:px-8 lg:px-12">
        <div style={{ color: "white" }} className="section-heading margin-center text-center text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-8 sm:mb-10 md:mb-12">
          Upcoming Events
        </div>
        {loading ? (
          <div className="loading-container flex flex-col items-center justify-center py-12 sm:py-16 md:py-20">
            <div className="loading-spinner w-12 h-12 sm:w-16 sm:h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <p style={{ color: 'white' }} className="mt-4 text-base sm:text-lg md:text-xl">Loading events...</p>
          </div>
        ) : error ? (
          <div className="error-container flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 text-center">
            <p style={{ color: 'white' }} className="text-base sm:text-lg md:text-xl mb-4">{error}</p>
          </div>
        ) : (
          <>
            {/* Mobile View - Single Event with Swipe and Dots */}
            <div className="md:hidden">
              <div 
                className="relative"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {latestEvents.length > 0 ? (
                  renderEventCard(latestEvents[currentMobileIndex])
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p style={{ color: 'white' }} className="text-base sm:text-lg">No upcoming events at the moment.</p>
                  </div>
                )}
              </div>

              {/* Dot Navigation */}
              {latestEvents.length > 1 && (
                <div className="flex justify-center gap-2 mt-6">
                  {latestEvents.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentMobileIndex(index)}
                      className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                        index === currentMobileIndex 
                          ? 'bg-white w-8' 
                          : 'bg-white/50 hover:bg-white/75'
                      }`}
                      aria-label={`Go to event ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Desktop View - Grid */}
            <div className="hidden md:grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {latestEvents.length > 0 ? (
                latestEvents.map(renderEventCard)
              ) : (
                <div className="col-span-1 sm:col-span-2 lg:col-span-3 flex flex-col items-center justify-center py-12 sm:py-16 md:py-20 text-center">
                  <p style={{ color: 'white' }} className="text-base sm:text-lg md:text-xl">No upcoming events at the moment.</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LatestEventsSection;